/**
 * Pure filter+sort that produces the candidate unit list for Step 4 from the
 * answers gathered in Step 3. No store dependency — caller passes units in.
 */

import type {
  BedroomPref,
  BudgetRange,
  Goal,
  InvestmentPriority,
  Unit,
  UnitType,
} from './types';

export interface CandidateFilters {
  goal: Goal;
  budget: BudgetRange;
  bedrooms: BedroomPref;
  /** Only meaningful when goal === 'investment'. */
  priority?: InvestmentPriority;
}

interface BudgetBucket {
  min: number;
  max: number;
}

const BUDGET_BUCKETS: Record<BudgetRange, BudgetBucket> = {
  under_25k: { min: 0, max: 25_000 },
  '25k_40k': { min: 25_000, max: 40_000 },
  '40k_50k': { min: 40_000, max: 50_000 },
  over_50k: { min: 50_000, max: Number.POSITIVE_INFINITY },
};

function inBudget(price: number, range: BudgetRange): boolean {
  const { min, max } = BUDGET_BUCKETS[range];
  if (range === 'under_25k') return price <= max;
  return price > min && price <= max;
}

function matchesBedrooms(unitBeds: number, pref: BedroomPref): boolean {
  if (pref === 'flex') return true;
  return unitBeds === pref;
}

/**
 * Returns ALL units that match the filters, sorted by:
 *   1. status priority    (available → reserved → sold)
 *   2. viewRank ASC       (lower = better view)
 *   3. floorNum DESC      (higher floor = better)
 *   4. basePrice ASC      (cheaper first within the same view+floor)
 *
 * Phase 8f-bcs (2026-05-08) — the previous MAX_RESULTS = 12 cap was
 * removed so the advisor sees the entire matching inventory in /match.
 * Status priority guarantees available units appear first; reserved
 * and sold cards are still rendered but disabled by UnitCard.
 */
export function filterCandidates(
  units: Unit[],
  filters: CandidateFilters
): Unit[] {
  const matched = units.filter(
    (u) =>
      matchesBedrooms(u.beds, filters.bedrooms) &&
      inBudget(u.basePrice, filters.budget)
  );

  const STATUS_PRIORITY: Record<typeof matched[number]['status'], number> = {
    available: 0,
    reserved:  1,
    sold:      2,
  };

  matched.sort((a, b) => {
    const sa = STATUS_PRIORITY[a.status];
    const sb = STATUS_PRIORITY[b.status];
    if (sa !== sb) return sa - sb;
    if (a.viewRank !== b.viewRank) return a.viewRank - b.viewRank;
    if (a.floorNum !== b.floorNum) return b.floorNum - a.floorNum;
    return a.basePrice - b.basePrice;
  });

  return matched;
}

/* ────────────────────────────────────────────────────────────
   Smart fallback: when no candidates match, propose the
   smallest tweak (open bedrooms / bump budget) that yields
   results, so the advisor turns rejection into a conversation.
   ──────────────────────────────────────────────────────────── */

const BUDGET_ORDER: ReadonlyArray<BudgetRange> = [
  'under_25k',
  '25k_40k',
  '40k_50k',
  'over_50k',
];

function nextBudget(b: BudgetRange): BudgetRange | null {
  const i = BUDGET_ORDER.indexOf(b);
  return i >= 0 && i < BUDGET_ORDER.length - 1 ? BUDGET_ORDER[i + 1] : null;
}

const TYPE_BY_BEDS: Record<1 | 2 | 3, UnitType> = {
  1: '1BHK',
  2: '2BHK',
  3: '3BHK',
};

export interface SmartFallback {
  /** Which fields the advisor would relax. Only the changed ones are present. */
  suggested: { bedrooms?: BedroomPref; budget?: BudgetRange };
  /** All units that satisfy the suggested filters, sorted by status priority. */
  candidates: Unit[];
  reason_ar: string;
  reason_en: string;
}

function fmtOMR(n: number): string {
  return n.toLocaleString('en-US');
}

function bedsLabel(beds: BedroomPref, lang: 'ar' | 'en'): string {
  if (beds === 'flex') return lang === 'ar' ? 'مرنة' : 'flexible bedrooms';
  if (lang === 'ar') {
    return beds === 1 ? 'غرفة واحدة' : beds === 2 ? 'غرفتين' : 'ثلاث غرف';
  }
  return `${beds}BHK`;
}

function buildReason(
  scenario: 'open-beds' | 'raise-budget' | 'both',
  units: Unit[],
  filters: CandidateFilters
): { reason_ar: string; reason_en: string } {
  const minPrice = Math.min(...units.map((u) => u.basePrice));
  const sampleType = units[0].type;
  const n = units.length;
  const bedsRequested = filters.bedrooms;
  const typeRequested =
    bedsRequested === 'flex'
      ? null
      : TYPE_BY_BEDS[bedsRequested as 1 | 2 | 3];

  switch (scenario) {
    case 'open-beds':
      return {
        reason_ar: `بميزانيتك، الأنسب ${sampleType} يبدأ من ${fmtOMR(minPrice)} ر.ع. وجدنا ${n} وحدة بغرف مرنة.`,
        reason_en: `Within your budget, ${sampleType} starts at OMR ${fmtOMR(minPrice)}. We found ${n} units with flexible bedrooms.`,
      };
    case 'raise-budget':
      return {
        reason_ar: `لـ ${bedsLabel(bedsRequested, 'ar')}، أقل سعر ${fmtOMR(minPrice)} ر.ع. وجدنا ${n} وحدة في الميزانية الأعلى.`,
        reason_en: `For ${typeRequested ?? bedsLabel(bedsRequested, 'en')}, the lowest price is OMR ${fmtOMR(minPrice)}. We found ${n} units in the next budget tier.`,
      };
    case 'both':
      return {
        reason_ar: `وجدنا ${n} وحدة عند توسيع الميزانية للأعلى وقبول غرف مرنة. الأقل: ${sampleType} من ${fmtOMR(minPrice)} ر.ع.`,
        reason_en: `We found ${n} units after raising the budget and allowing flexible bedrooms. The cheapest is ${sampleType} from OMR ${fmtOMR(minPrice)}.`,
      };
  }
}

/**
 * Try increasingly relaxed filters to find SOMETHING to show. Returns null
 * only when no combination within one step of the user's answers yields hits.
 */
export function suggestFallback(
  units: Unit[],
  filters: CandidateFilters
): SmartFallback | null {
  // Step 1 — keep budget, open bedrooms to flex (only meaningful if user picked a specific number)
  if (filters.bedrooms !== 'flex') {
    const tryFlex = filterCandidates(units, { ...filters, bedrooms: 'flex' });
    if (tryFlex.length > 0) {
      return {
        suggested: { bedrooms: 'flex' },
        candidates: tryFlex,
        ...buildReason('open-beds', tryFlex, filters),
      };
    }
  }

  // Step 2 — keep bedrooms, bump budget by one tier
  const upBudget = nextBudget(filters.budget);
  if (upBudget) {
    const tryUp = filterCandidates(units, { ...filters, budget: upBudget });
    if (tryUp.length > 0) {
      return {
        suggested: { budget: upBudget },
        candidates: tryUp,
        ...buildReason('raise-budget', tryUp, filters),
      };
    }

    // Step 3 — bump budget AND open bedrooms (only if user picked a specific number)
    if (filters.bedrooms !== 'flex') {
      const tryBoth = filterCandidates(units, {
        ...filters,
        budget: upBudget,
        bedrooms: 'flex',
      });
      if (tryBoth.length > 0) {
        return {
          suggested: { budget: upBudget, bedrooms: 'flex' },
          candidates: tryBoth,
          ...buildReason('both', tryBoth, filters),
        };
      }
    }
  }

  // No nearby relaxation works — advisor takeover.
  return null;
}
